/**
 * Team → Timesheets → Automation ideas (prototype tsAutomate). Repeating non-client tasks the server found in
 * the timesheets (GET /timesheets/automation-candidates for the month); tick → "Add ticked to the automation
 * sheet" (POST …/{id} in_sheet + status Idea); the sheet's status (Idea → Live) and Remove; "Download as
 * Excel" is the server's CSV (UTF-8 with BOM, the prototype's columns).
 */
import { useState } from 'react';
import { AUTOMATION_SIZE_LABEL, pastedSheet, type AutomationCandidate, type CreateAutomationBody, type UpdateAutomationBody } from '@amplispot/contracts';
import { buildPath, routes } from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../../api';
import { firstName } from '../../lib/format';
import { useSession } from '../../lib/session';
import { fh, useAutomation, useTsWrite } from '../../lib/timesheets';
import { Empty, ErrorState, Loading, errorText, useToast } from '../../ui/kit';
import { copyText } from './TsModals';

const STATUSES = ['Idea', 'Planned', 'Building', 'Live'] as const;
const SIZE_CHIP = { S: ['hot', 'Quick job'], M: ['warm', 'Medium job'], L: ['cold', 'Big job'] } as const;
const NUM = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
const who = (a: AutomationCandidate) => a.done_by.map((u) => firstName(u.name)).join(', ');

export function TsAutomate({ month }: { month: string }) {
  const q = useAutomation(month);
  const toast = useToast();
  // POST /timesheets/automation-candidates is for managers (timesheet.read.all); anyone else would only get a 403.
  const canAdd = useSession().can('timesheet.read.all');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const upd = useTsWrite((v: { id: string; body: UpdateAutomationBody }) => call('automation.update', { params: { id: v.id }, body: v.body }));
  // Prototype "Add one of your own" (autoAddOwn): POST /timesheets/automation-candidates → in the sheet, status Idea.
  const [own, setOwn] = useState({ t: '', h: '', f: '' });
  const add = useTsWrite((body: CreateAutomationBody) => call('automation.create', { body, idempotencyKey: newIdempotencyKey() }));
  const addOwn = () => {
    const title = own.t.trim();
    const hours = Number(own.h);
    if (title.length < 3) return toast('Say what the task is');
    if (own.h.trim() === '' || !(hours >= 0)) return toast('Say about how many hours a week it takes');
    // CreateAutomationBody.fix is optional but at least 3 characters: block a 1–2 character idea rather than drop it.
    const fix = own.f.trim();
    if (fix.length > 0 && fix.length < 3) return toast('How it could be automated: at least 3 characters');
    add.mutate(
      { title, hours_per_week: hours, fix: fix || undefined, period: month },
      {
        onSuccess: () => {
          setOwn({ t: '', h: '', f: '' });
          toast('Added to the automation sheet');
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  };

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const all = q.data ?? [];
  // "Found in the timesheets" = detected from someone's logged work; one added by hand ("Add one of your own")
  // has no one behind it and lives only in the sheet, as in the prototype.
  const found = all.filter((a) => a.done_by.length > 0);
  const sheet = all.filter((a) => a.in_sheet);
  const nSel = found.filter((a) => sel[a.id] && !a.in_sheet).length;
  const tot = found.reduce((x, a) => x + a.hours_per_week, 0);
  const pk = sheet.reduce((x, a) => x + a.hours_per_week, 0);
  const csvHref = `${buildPath(routes['automation.csv'].path)}?period=${month}`;

  const pick = async () => {
    const ids = found.filter((a) => sel[a.id] && !a.in_sheet);
    if (!ids.length) return toast('Tick a few tasks first');
    try {
      for (const a of ids) await upd.mutateAsync({ id: a.id, body: { in_sheet: true, status: a.status ?? 'Idea' } });
      toast(`${ids.length} task${ids.length > 1 ? 's' : ''} added to the automation sheet`);
      setSel({});
    } catch (e) {
      toast(errorText(e));
    }
  };
  const copy = () =>
    copyText(
      // pastedSheet: a tab or line break inside a title or idea would otherwise split the paste and open a new cell
      // with '=' (sf-577ff216, review of t-8dd07d04).
      pastedSheet([['Task', 'How often', 'Hours a week', 'Done by', 'Idea', 'Status'], ...sheet.map((a) => [a.title, a.how_often, String(a.hours_per_week), who(a), a.fix ?? '', a.status ?? ''])]),
      () => toast('Copied. Paste it into a sheet.'),
    );

  return (
    <>
      <div className="sub" style={{ maxWidth: 720, margin: '0 0 18px', fontSize: 13.5 }}>
        Repeating work that the team does by hand. Tick the ones worth automating, add them to the sheet, then download it as Excel.
        {found.length ? (
          <>
            {' '}
            About <b style={{ color: 'var(--ink)' }}>{fh(tot)} a week</b> across the team is in {found.length === 1 ? 'this' : 'these'} {NUM[found.length] ?? found.length}.
          </>
        ) : null}
      </div>
      <div className="panel">
        <div className="hd">
          <h3>Repeating tasks found in the timesheets</h3>
          <div className="r">{nSel ? `${nSel} ticked` : ''}</div>
        </div>
        <div className="bd" style={{ display: 'grid', gap: 10 }}>
          {found.length ? (
            <>
              {found.map((a) => (
                <label key={a.id} className={`aut${sel[a.id] ? ' on' : ''}${a.in_sheet ? ' done' : ''}`}>
                  <input type="checkbox" checked={!!sel[a.id] || a.in_sheet} disabled={a.in_sheet} onChange={(e) => setSel((x) => ({ ...x, [a.id]: e.target.checked }))} />
                  <div className="grow">
                    <b>{a.title}</b>
                    <div className="sub" style={{ fontSize: 12.5, marginTop: 2 }}>
                      {a.how_often} · {who(a) || `${a.occurrences} time${a.occurrences === 1 ? '' : 's'}`} · about <b style={{ color: 'var(--ink)' }}>{a.hours_per_week} h a week</b>
                    </div>
                    {a.fix ? <div style={{ fontSize: 13, marginTop: 6 }}>{a.fix}</div> : null}
                  </div>
                  {a.size ? (
                    <span className={`chip ${SIZE_CHIP[a.size][0]}`} title={AUTOMATION_SIZE_LABEL[a.size]}>
                      {SIZE_CHIP[a.size][1]}
                    </span>
                  ) : null}
                  {a.in_sheet ? <span className="chip info">In the sheet</span> : null}
                </label>
              ))}
              <div className="split" style={{ gap: 10, marginTop: 6 }}>
                <button className="btn pri" onClick={() => void pick()} disabled={upd.isPending}>
                  Add ticked to the automation sheet
                </button>
                <div className="sub">{nSel ? `${nSel} ticked` : 'Tick the tasks you want to add'}</div>
              </div>
            </>
          ) : (
            <Empty title="No repeating tasks found this month">A task shows here once the same kind of internal work is logged three or more times in the month.</Empty>
          )}
        </div>
      </div>
      <div className="panel" style={{ marginTop: 22 }}>
        <div className="hd">
          <h3>Automation sheet</h3>
          <div className="r">{sheet.length ? `${sheet.length} task${sheet.length > 1 ? 's' : ''}, ${fh(pk)} a week` : ''}</div>
        </div>
        <div className="bd" style={{ overflowX: 'auto' }}>
          {sheet.length ? (
            <>
              <table style={{ minWidth: 620 }}>
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>How often</th>
                    <th className="tr">Hours a week</th>
                    <th>Idea</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sheet.map((a) => (
                    <tr key={a.id}>
                      <td style={{ minWidth: 200 }}>
                        <b>{a.title}</b>
                        <div className="sub" style={{ fontSize: 12 }}>
                          {who(a) || (a.added_by ? `Added by ${firstName(a.added_by.name)}` : '')}
                        </div>
                      </td>
                      <td>{a.how_often}</td>
                      <td className="tr num">{a.hours_per_week}</td>
                      <td style={{ minWidth: 240, fontSize: 13 }}>{a.fix ?? <span className="sub">To be worked out</span>}</td>
                      <td>
                        <select
                          className="in"
                          style={{ width: 'auto', borderRadius: 99 }}
                          value={a.status ?? 'Idea'}
                          aria-label="Status"
                          onChange={(e) => upd.mutate({ id: a.id, body: { status: e.target.value as (typeof STATUSES)[number] } }, { onError: (er) => toast(errorText(er)) })}
                        >
                          {STATUSES.map((s) => (
                            <option key={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button className="btn sm ghost" onClick={() => upd.mutate({ id: a.id, body: { in_sheet: false } }, { onError: (er) => toast(errorText(er)) })}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="split" style={{ gap: 10, marginTop: 14 }}>
                <a className="btn pri" href={csvHref} download onClick={() => toast('Downloaded. It opens in Excel.')}>
                  Download as Excel
                </a>
                <button className="btn" onClick={copy}>
                  Copy to paste into a sheet
                </button>
              </div>
            </>
          ) : (
            <div className="empty">
              <b>Nothing in the sheet yet</b>Tick tasks above and add them.
            </div>
          )}
          {canAdd ? (
            <>
              <div className="lbl" style={{ margin: '22px 0 8px' }}>
                Add one of your own
              </div>
              <div className="split" style={{ gap: 10, flexWrap: 'wrap' }}>
                <input className="in" placeholder="The task" style={{ flex: 2, minWidth: 180 }} value={own.t} onChange={(e) => setOwn((o) => ({ ...o, t: e.target.value }))} aria-label="The task" />
                <input
                  className="in"
                  type="number"
                  step={0.5}
                  min={0}
                  placeholder="Hours a week"
                  style={{ width: 130 }}
                  value={own.h}
                  onChange={(e) => setOwn((o) => ({ ...o, h: e.target.value }))}
                  aria-label="Hours a week"
                />
                <input
                  className="in"
                  placeholder="How it could be automated"
                  style={{ flex: 2, minWidth: 180 }}
                  value={own.f}
                  onChange={(e) => setOwn((o) => ({ ...o, f: e.target.value }))}
                  aria-label="How it could be automated"
                />
                <button className="btn" onClick={addOwn} disabled={add.isPending}>
                  Add
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
